import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, ArrowRight, Brush, Leaf } from "lucide-react";

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">{children}</span>
);

function ServiceCard({ s, navigate }) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl bg-white border border-[#E2E8E4]/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300" data-testid={`service-card-${s.service_id}`}>
      <div className="aspect-[16/10] overflow-hidden">
        <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-baseline justify-between">
          <h3 className="font-heading text-2xl font-semibold text-[#1A362D]">{s.name}</h3>
          <div className="text-right">
            <p className="font-heading text-2xl font-semibold text-[#C47B57]">${s.price}</p>
            <p className="text-xs text-[#8A9A8C]">{s.price_unit}</p>
          </div>
        </div>
        <p className="mt-1 text-sm uppercase tracking-wider text-[#8FA38C]">{s.tagline}</p>
        <p className="mt-3 text-[#4A5D4E] font-light text-sm">{s.description}</p>
        <ul className="mt-4 space-y-2 flex-1">
          {s.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-[#1A362D]">
              <Check className="h-4 w-4 text-[#8FA38C]" /> {f}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex gap-3">
          <Button onClick={() => navigate(`/booking?service=${s.service_id}`)} data-testid={`book-${s.service_id}`} className="flex-1 bg-[#1A362D] hover:bg-[#254A3E] text-white rounded-full">
            Book Now
          </Button>
          <Button onClick={() => navigate("/contact")} variant="outline" className="rounded-full border-[#8FA38C] text-[#1A362D]">
            Quote
          </Button>
        </div>
        <p className="mt-3 text-xs text-[#8A9A8C] text-center">Est. duration: {s.duration}</p>
      </div>
    </div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const cleaning = services.filter((s) => s.category === "cleaning");
  const landscaping = services.filter((s) => s.category === "landscaping");

  return (
    <div className="bg-[#FAFAFA]">
      <section className="bg-[#1A362D] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">Our Services</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight">
            Transparent pricing. Exceptional care.
          </h1>
          <p className="mt-5 text-white/80 text-lg font-light max-w-2xl mx-auto">
            Choose a service below or request a custom quote. Every job is backed by our satisfaction guarantee.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Tabs defaultValue="cleaning" className="w-full">
          <TabsList className="mx-auto flex w-fit bg-[#F2F4F2] p-1.5 rounded-full mb-12" data-testid="services-tabs">
            <TabsTrigger value="cleaning" className="rounded-full px-8 py-2.5 data-[state=active]:bg-white data-[state=active]:text-[#1A362D] data-[state=active]:shadow-sm text-[#4A5D4E]">
              <Brush className="h-4 w-4 mr-2" /> Cleaning
            </TabsTrigger>
            <TabsTrigger value="landscaping" className="rounded-full px-8 py-2.5 data-[state=active]:bg-white data-[state=active]:text-[#1A362D] data-[state=active]:shadow-sm text-[#4A5D4E]">
              <Leaf className="h-4 w-4 mr-2" /> Landscaping
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cleaning">
            <Overline>Interior care</Overline>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7 mt-4">
              {cleaning.map((s) => <ServiceCard key={s.service_id} s={s} navigate={navigate} />)}
            </div>
          </TabsContent>

          <TabsContent value="landscaping">
            <Overline>Outdoor care</Overline>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7 mt-4">
              {landscaping.map((s) => <ServiceCard key={s.service_id} s={s} navigate={navigate} />)}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-16 rounded-2xl bg-white border border-[#E2E8E4]/60 p-10 text-center">
          <h3 className="font-heading text-2xl font-semibold text-[#1A362D]">Not sure what you need?</h3>
          <p className="mt-2 text-[#4A5D4E] font-light">Tell us about your space and we'll recommend the perfect plan.</p>
          <Button onClick={() => navigate("/contact")} className="mt-5 bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full px-8">
            Request a Custom Quote <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
